import type React from 'react'
import { useEffect, useState } from 'react'
import { useForm, FormProvider, FieldValue, FieldValues } from 'react-hook-form'
import { Button } from '../../../components/ui/Button'
import { UserDataSection } from './UserData'
import { UserSettingsSection } from './UserSettings'
import { useEditUser } from '../../../api/auth/useEditUser'
import toast from 'react-hot-toast'
import { useGetUser } from '../../../api/auth/useGetUser'
import { queryClient } from '../../../main'

interface UserData {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  dateOfBirth: string
}

interface UserSettings {
  receiveNotifications: boolean
  darkMode: boolean
  language: string
}

const mockUserSettings: UserSettings = {
  receiveNotifications: true,
  darkMode: false,
  language: 'en',
}

export const UserProfilePage: React.FC = () => {
  const [isEditingUserData, setIsEditingUserData] = useState(false)
  const [isEditingSettings, setIsEditingSettings] = useState(false)
  const { mutateAsync: editUser } = useEditUser()
  const { data: user } = useGetUser()

  const formContext = useForm()

  const { handleSubmit } = formContext

  const settingsMethods = useForm<UserSettings>({
    defaultValues: mockUserSettings,
  })

  const onSubmitUserData = async (values: FieldValues) => {
    const toastId = toast.loading('Updating data')

    await editUser(
      {
        user_id: Number(user?.id),
        data: {
          first_name: values?.first_name,
          last_name: values?.last_name,
          email: values?.email,
          phone: values?.phone,
        },
      },
      {
        onSuccess: async (data) => {
          toast.success('Settings updated!', { id: toastId, duration: 2000 })
          await queryClient.invalidateQueries(['currentUser'])
        },
        onError: (err) => {
          toast.error('An error occured', { id: toastId })
        },
      },
    )

    setIsEditingUserData(false)
  }

  const onSubmitSettings = async (values: FieldValues) => {
    const toastId = toast.loading('Updating data')

    await editUser(
      {
        user_id: Number(user?.id),
        data: {
          target_currency_id: values?.target_currency?.value,
          language: values?.language?.value,
        },
      },
      {
        onSuccess: async (data) => {
          toast.success('Settings updated!', { id: toastId, duration: 2000 })
          await queryClient.invalidateQueries(['currentUser'])
        },
        onError: (err) => {
          toast.error('An error occured', { id: toastId })
        },
      },
    )
    setIsEditingSettings(false)
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8 bg-foreground/10 dark:bg-slate-800">
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="bg-foreground/20 dark:bg-slate-600 shadow overflow-hidden rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <h2 className="text-md font-bold leading-7 text-foreground dark:text-white  sm:truncate">
              Modify subscription
            </h2>

            <Button
              onClick={() =>
                window.open(
                  `https://billing.stripe.com/p/login/6oE15F5YceDTb4Y000?prefilled_email=${user?.email}`,
                )
              }>
              Edit
            </Button>
          </div>
        </div>

        <div className="bg-foreground/20 dark:bg-slate-600 shadow overflow-hidden rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <h2 className="text-2xl font-bold leading-7 text-foreground dark:text-white sm:text-3xl sm:truncate">
              My Profile
            </h2>
            {!isEditingUserData && (
              <Button onClick={() => setIsEditingUserData(true)}>Edit</Button>
            )}
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            <FormProvider {...formContext}>
              <form onSubmit={handleSubmit(onSubmitUserData)}>
                <UserDataSection
                  isEditing={isEditingUserData}
                  onCancel={() => setIsEditingUserData(false)}
                />
              </form>
            </FormProvider>
          </div>
        </div>

        <div className="bg-foreground/20 dark:bg-slate-600 shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <h2 className="text-2xl font-bold leading-7 text-foreground dark:text-white sm:text-3xl sm:truncate">
              My Settings
            </h2>
            {!isEditingSettings && (
              <Button onClick={() => setIsEditingSettings(true)}>Edit</Button>
            )}
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            <FormProvider {...settingsMethods}>
              <form onSubmit={settingsMethods.handleSubmit(onSubmitSettings)}>
                <UserSettingsSection
                  isEditing={isEditingSettings}
                  onCancel={() => setIsEditingSettings(false)}
                />
              </form>
            </FormProvider>
          </div>
        </div>
      </div>
    </div>
  )
}
